/**
 * Load Group Tool
 *
 * Loads a Yap project document (.yap/.json) as a Model Group: the file's
 * elements are instantiated into the host model at the clicked point and
 * bound to a GroupElement through real member ids, so the existing group
 * tooling (ungroup, member count) operates on actual content. The
 * placement preview shows the measured extents of the loaded content.
 *
 * Binary Revit files (.rvt) cannot be decoded in this build and are
 * refused with an explicit message — an empty group is never fabricated.
 */

import { v4 as uuidv4 } from 'uuid';
import { GroupElement } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import {
    parseLinkedProjectText,
    instantiateLinkedElements,
    bboxSize,
    BBox,
} from '../services/linked/LinkedProjectService';
import i18n from '../i18n';

export class LoadGroupTool extends BaseTool {
    id = 'load_group';
    name = 'Load as Group';
    cursor = 'copy';

    /** Parsed source content awaiting placement. */
    private pickedFile: { name: string; payload: Exclude<ReturnType<typeof parseLinkedProjectText>, null>; bbox: BBox } | null = null;

    onActivate(): void {
        // Dialog-driven command: BIM opens a file dialog for Load as Group.
        void this.pickFile('.yap,.json').then(async (file) => {
            if (!file) {
                this.exitToSelect();
                return;
            }
            if (/\.rvt$/i.test(file.name)) {
                this.notify(i18n.t('tools.notify.revitBinaryUnsupported', { fileName: file.name }), 'error');
                this.exitToSelect();
                return;
            }
            const text = await file.text();
            const payload = parseLinkedProjectText(text);
            if (!payload) {
                this.notify(i18n.t('tools.notify.groupParseFailed', { fileName: file.name }), 'error');
                this.exitToSelect();
                return;
            }
            this.pickedFile = { name: file.name, payload, bbox: instantiateBBox(payload) };
            this.notify(i18n.t('tools.notify.groupFileSelected', { fileName: file.name }));
        });
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.pickedFile) return;
        if (event.button !== 0) return;

        // Insert the picked file as a group at the clicked location.
        const point = this.getIntersection(event);
        if (point) {
            this.createGroup(this.snapToGrid(point));
            this.exitToSelect(); // Single placement, then back to Select
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (!this.pickedFile) return;
        const point = this.getIntersection(event);
        if (point) {
            this.updatePreview(this.snapToGrid(point));
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private updatePreview(point: THREE.Vector3) {
        if (!this.context || !this.pickedFile) return;

        const size = bboxSize(this.pickedFile.bbox);
        this.context.appStore.setToolPreview({
            type: 'box',
            position: point,
            // Real measured extents of the document being placed.
            size: [size[0], size[1], size[2]],
            color: '#aaaaaa',
            opacity: 0.5
        });
    }

    private createGroup(position: THREE.Vector3): GroupElement | null {
        if (!this.context || !this.pickedFile) return null;

        const { name, payload, bbox } = this.pickedFile;
        this.pickedFile = null;

        // Place the group's measured centre at the clicked point (ground
        // plane follows the click elevation).
        const centreX = (bbox.min[0] + bbox.max[0]) / 2;
        const centreY = (bbox.min[1] + bbox.max[1]) / 2;
        const offset: [number, number, number] = [
            position.x - centreX,
            position.y - centreY,
            position.z - bbox.min[2],
        ];

        // 1. Instantiate the source content as real host elements.
        const { elements, bbox: placedBBox } = instantiateLinkedElements(payload.elementEntries, uuidv4(), { offset });
        const memberIds: string[] = [];
        for (const el of elements) {
            this.context.bimStore.addElement(el as any);
            memberIds.push(el.id);
        }

        // 2. Bind them through a GroupElement with real member ids.
        const group: GroupElement = {
            id: uuidv4(),
            category: 'Model Groups',
            type: `Group: ${name}`,
            parameters: {
                'Origin Level': 'Level 1',
                'Reference Level': 'Level 1',
                'Members': String(memberIds.length),
            },
            constraints: [],
            geometryData: {
                origin: [position.x, position.y, position.z],
                memberIds
            }
        };

        this.context.bimStore.addElement(group);
        const size = bboxSize(placedBBox);
        this.notify(
            i18n.t('tools.notify.groupLoaded', { fileName: name, count: memberIds.length, size: `${size[0].toFixed(1)}×${size[1].toFixed(1)} m` }),
            'success'
        );
        return group;
    }

    /**
     * Programmatic entry with an already-parsed payload (macros/tests):
     * places the document content as a group at the given point.
     */
    public executeGroupFromPayload(fileName: string, payload: NonNullable<ReturnType<typeof parseLinkedProjectText>>, position: THREE.Vector3): GroupElement | null {
        this.pickedFile = { name: fileName, payload, bbox: instantiateBBox(payload) };
        const group = this.createGroup(position);
        this.pickedFile = null;
        return group;
    }
}

/** One-off extents for the preview before placement instantiates anything. */
function instantiateBBox(payload: NonNullable<ReturnType<typeof parseLinkedProjectText>>): BBox {
    // instantiateLinkedElements is the single source of truth for measuring;
    // run it on a throwaway link id (no elements are added to any store).
    return instantiateLinkedElements(payload.elementEntries, 'measure-preview').bbox;
}
